import { pdf } from "@react-pdf/renderer";
import { useEffect, useState, useRef } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import DownloadButton from "../../../../common/batches/DownloadButton";
import ViewButton from "../../../../common/batches/ViewButton";
import AuctionERPBreadcrumb from "../../../../common/Breadcrumb/AuctionERPBreadcumb";
import {
  currencyToNumber,
  indianCurrencyFormater,
} from "../../../../helpers/functions/currencyFormater";
import { errorToast, successToast, warningToast } from "../../../../react-toastfiy/toast";
import AadharConcentandMTBLAggrementDocumentPDF from "../common/AadharConcentandMTBLAggrement";
import DPNDocumentPDF from "../common/DPNPdf";
import GSTDeclarationDocumentPDF from "../common/GSTDeclarationPdf";
import MTSanctionLetterPDF from "../common/MTSanctionLetterPdf";
import MTApplicationFormPDF from "../common/MTApplicationFormPdf";
import MTUDCDocumentPDF from "../common/MTUDCPdf";
import SignatureVerificationDocumentPDF from "../common/SignatureVerificationPdf";
import TFUDCDocumentPDF from "../common/TFUDCPdf";
import useLimitSettingDocumentsCollection from "../hooks/useLimitSettingDocumentsCollection";
import taService from "../services/ta.service";
import { rupeesToWords } from "../../../../helpers/functions/rupeesToWords";
import { 
  saveFormDataToLocalStorage, 
  clearFormDataFromLocalStorage,
  formatDraftTimestamp 
} from "../../../../helpers/functions/localStorageHelper";
import { PDFDocument } from "pdf-lib";
import { mtblAgreementLabel, risk_head_role } from "../common/fields";

const LimitSettingDocumentCollection = () => {
  const location = useLocation();
  const data = location?.state.value;
  const [limitSettingData, setLimitSettingData] = useState({});
  const {
    formData,
    setFormData,
    setButtonLoader,
    user,
    history,
    isDraftLoaded,
    setIsDraftLoaded,
    draftTimestamp,
    clearDraft,
  } = useLimitSettingDocumentsCollection({
    limitData: data
  });

  const [pdfBase64, setPdfBase64] = useState("");

  const [loader, setLoader] = useState(false);
  const [generatingRefNumber, setGeneratingRefNumber] = useState(false);
  const [docOrder, setDocOrder] = useState([]);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergingPdf, setMergingPdf] = useState(false);
  // Per-document Risk Head signing choice, keyed by merge doc key. A missing
  // key means "use the default" (RH_DEFAULT_DOCS); the Buyer signs every doc.
  const [docRhSign, setDocRhSign] = useState({});
  // Stamp paper: the File picked this session (for merging) and its stored link.
  const [stampPaperFile, setStampPaperFile] = useState(null);
  const [stampPaperLink, setStampPaperLink] = useState("");
  const [uploadingStampPaper, setUploadingStampPaper] = useState(false);
  const stampPaperInputRef = useRef(null);
  // The merged PDF must be viewed or downloaded before the consent can be ticked.
  const [mergedViewed, setMergedViewed] = useState(false);
  const [esignConsent, setEsignConsent] = useState(false);
  const [buyerAadharName, setBuyerAadharName] = useState("");
  // Stamp paper expiry (YYYY-MM-DD), required before sending for e-sign.
  const [stampPaperExpiry, setStampPaperExpiry] = useState("");
  const [sendingEsign, setSendingEsign] = useState(false);
  // 1 = choose documents & signers, 2 = stamp paper, review, name, consent.
  const [mergeStep, setMergeStep] = useState(1);

  // Documents accordion: id of the open item, one at a time. Controlled here
  // rather than via Bootstrap's data-bs-toggle, which fails to close when a
  // second Bootstrap bundle is loaded elsewhere in the app.
  const [openDoc, setOpenDoc] = useState(null);
  const toggleDoc = (id) => setOpenDoc((prev) => (prev === id ? null : id));

  // Ref for debouncing auto-save
  const autoSaveTimeoutRef = useRef(null);

  useEffect(() => {
    document.title = "Limit Setting Documents Collection";
  }, []);

  const handleGenerateReferenceNumber = async () => {
    try {
      setGeneratingRefNumber(true);
      const formDataToSend = new FormData();
      formDataToSend.append("taOnboardingCaseId", data?.taOnboardingCaseId);

      const response = await taService.GenerateReferenceNumber(formDataToSend);

      if (response?.status === 200) {
        const refNumber = response?.data?.taFundingByMTReferenceNumber || response?.taFundingByMTReferenceNumber;
        
        // Update formData with the reference number
        setFormData((prev) => ({
          ...prev,
          referenceNumber: refNumber,
        }));
        
        // Update limitSettingData with reference number and flag
        setLimitSettingData((prev) => ({
          ...prev,
          taFundingByMTReferenceNumber: refNumber,
          isTAFundingByMTReferenceNumberGenerated: true,
        }));
        
        successToast(response?.msg || "Reference number generated successfully");
      } else {
        errorToast(response?.msg || "Failed to generate reference number");
      }
    } catch (error) {
      console.error("Error generating reference number:", error);
      errorToast(error?.message || "Something went wrong while generating reference number");
    } finally {
      setGeneratingRefNumber(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePartnerChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...(prev.partnerDetails || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, partnerDetails: updated };
    });
  };

  const dealerName =
    (
      (formData?.personName || formData?.companyName || "dealer")
        .trim()
        .replace(/[^a-zA-Z0-9 ]/g, "")
        .replace(/\s+/g, "_")
    ) || "dealer";

  // All mergeable documents — order here defines left-panel order (A to Z,
  // same as the Documents list)
  const allMergeDocs = [
    { key: "aadharConsent",   label: "Aadhar Consent",         show: true },
    { key: "dpn",             label: "DPN Document",           show: true },
    { key: "gstDeclaration",  label: "GST Declaration",        show: true },
    { key: "mtAppForm",       label: "MT Application Form",    show: !!formData?.is_ta_funding_by_mt },
    { key: "mtSanctionLetter",label: "MT Sanction Letter",     show: !!formData?.is_ta_funding_by_mt },
    { key: "mtudc",           label: "MT UDC",                 show: true },
    { key: "mtblAgreement",   label: "MTBL Agreement",         show: true },
    { key: "sigVerify",       label: "Signature Verification", show: true },
    { key: "tfudc",           label: "TF UDC",                 show: !formData?.is_ta_funding_by_mt },
  ];

  // Documents the Risk Head signs by default (editable per document).
  const RH_DEFAULT_DOCS = ["mtSanctionLetter", "mtblAgreement"];
  const needsRhSign = (key) => docRhSign[key] ?? RH_DEFAULT_DOCS.includes(key);
  const toggleRhSign = (key) =>
    setDocRhSign((prev) => ({ ...prev, [key]: !needsRhSign(key) }));

  const toggleDocSelection = (key) => {
    if (docOrder.includes(key)) {
      // Removing a doc forgets its signer choice, so re-adding starts from the default.
      setDocRhSign(({ [key]: _removed, ...rest }) => rest);
    }
    setDocOrder((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const resetMergeSelection = () => {
    setDocOrder([]);
    setDocRhSign({});
  };

  const moveDocUp = (key) => {
    setDocOrder((prev) => {
      const idx = prev.indexOf(key);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };

  const moveDocDown = (key) => {
    setDocOrder((prev) => {
      const idx = prev.indexOf(key);
      if (idx < 0 || idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };

  const handlePreviewPDF = async () => {
    const blob = await pdf(<DPNDocumentPDF formData={formData} />).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url);
  };
  const handleDownloadPDF = async () => {
    const blob = await pdf(<DPNDocumentPDF formData={formData} />).toBlob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `DPN document - ${dealerName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };
  const handleDownloadPDFMTBLAgreement = async () => {
    const blob = await pdf(
      <AadharConcentandMTBLAggrementDocumentPDF formData={formData} part="mtbl" />
    ).toBlob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `MTBL Agreement - ${dealerName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };
  const handleDownloadPDFAadharConsent = async () => {
    const blob = await pdf(
      <AadharConcentandMTBLAggrementDocumentPDF formData={formData} part="consent" />
    ).toBlob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `Aadhar Consent - ${dealerName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };
  const handleDownloadPDFMTUDC = async () => {
    const blob = await pdf(<MTUDCDocumentPDF formData={formData} />).toBlob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `MTUDC document - ${dealerName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };
  const handleDownloadPDFTFUDC = async () => {
    const blob = await pdf(<TFUDCDocumentPDF formData={formData} />).toBlob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `TFUDC document - ${dealerName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };
  const handlePreviewPDFForMTUDC = async () => {
    const blob = await pdf(<MTUDCDocumentPDF formData={formData} />).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url);
  };
  const handlePreviewPDFForTFUDC = async () => {
    const blob = await pdf(<TFUDCDocumentPDF formData={formData} />).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url);
  };

  const handlePreviewPDFForSignatureVerification = async () => {
    const blob = await pdf(
      <SignatureVerificationDocumentPDF formData={formData} />
    ).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url);
  };

  const handleDownloadPDFSignatureVerification = async () => {
    const blob = await pdf(
      <SignatureVerificationDocumentPDF formData={formData} />
    ).toBlob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `Signature Verification document - ${dealerName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const handlePreviewPDFForGSTDeclaration = async () => {
    const blob = await pdf(
      <GSTDeclarationDocumentPDF formData={formData} />
    ).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url);
  };

  const handleDownloadPDFGSTDeclaration = async () => {
    const blob = await pdf(
      <GSTDeclarationDocumentPDF formData={formData} />
    ).toBlob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `GST Declaration document - ${dealerName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const handlePreviewAadharConsent = async () => {
    const blob = await pdf(
      <AadharConcentandMTBLAggrementDocumentPDF formData={formData} part="consent" />
    ).toBlob();
    window.open(URL.createObjectURL(blob));
  };

  const handlePreviewMTBLAgreement = async () => {
    const blob = await pdf(
      <AadharConcentandMTBLAggrementDocumentPDF formData={formData} part="mtbl" />
    ).toBlob();

    const url = URL.createObjectURL(blob);
    window.open(url);

    const reader = new FileReader();

    reader.onloadend = () => {
      const base64String = reader.result.split(",")[1];
      setPdfBase64(base64String);
    };

    reader.readAsDataURL(blob);
  };

  const handlePreviewMTSanctionLetter = async () => {
    const blob = await pdf(<MTSanctionLetterPDF formData={formData} />).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url);
  };

  const handleDownloadMTSanctionLetter = async () => {
    const blob = await pdf(<MTSanctionLetterPDF formData={formData} />).toBlob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `MT Sanction Letter - ${dealerName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const handlePreviewMTApplicationForm = async () => {
    const blob = await pdf(<MTApplicationFormPDF formData={formData} limitSettingData={limitSettingData} />).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url);
  };

  const handleDownloadMTApplicationForm = async () => {
    const blob = await pdf(<MTApplicationFormPDF formData={formData} limitSettingData={limitSettingData} />).toBlob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `MT Application Form - ${dealerName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  // Any change to what goes into the merged PDF invalidates the earlier view.
  useEffect(() => {
    setMergedViewed(false);
    setEsignConsent(false);
  }, [docOrder, docRhSign, stampPaperFile, stampPaperLink]);

  // A previously saved stamp paper is shown when the case loads.
  useEffect(() => {
    if (limitSettingData?.limitSettingStampPaper) {
      setStampPaperLink(limitSettingData.limitSettingStampPaper);
    }
  }, [limitSettingData?.limitSettingStampPaper]);

  useEffect(() => {
    if (limitSettingData?.limitSettingStampPaperExpiry) {
      setStampPaperExpiry(limitSettingData.limitSettingStampPaperExpiry);
    }
  }, [limitSettingData?.limitSettingStampPaperExpiry]);

  const openMergeModal = () => {
    setBuyerAadharName((prev) => prev || formData?.personName || data?.personName || "");
    setMergeStep(1);
    setShowMergeModal(true);
  };

  const handleStampPaperUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      errorToast("Stamp paper must be a PDF.");
      return;
    }
    try {
      // Fail early on PDFs pdf-lib can't merge (e.g. password protected).
      await PDFDocument.load(await file.arrayBuffer());
    } catch (error) {
      errorToast("This PDF can't be read. Please upload an unprotected PDF.");
      return;
    }
    setUploadingStampPaper(true);
    try {
      const fd = new FormData();
      fd.append("taOnboardingCaseId", data?.taOnboardingCaseId);
      fd.append("docType", "stamp_paper");
      fd.append("file", file);
      if (stampPaperExpiry) fd.append("stampPaperExpiry", stampPaperExpiry);
      const res = await taService.UploadLimitSettingEsignDoc(fd);
      if (res?.status === 200) {
        setStampPaperFile(file);
        setStampPaperLink(res.link);
        successToast("Stamp paper uploaded");
      } else {
        errorToast(res?.msg || "Stamp paper upload failed");
      }
    } finally {
      setUploadingStampPaper(false);
    }
  };

  // Bytes of the stamp paper: the file picked this session, else the saved one.
  const getStampPaperBytes = async () => {
    if (stampPaperFile) return stampPaperFile.arrayBuffer();
    try {
      const res = await fetch(stampPaperLink);
      if (!res.ok) throw new Error(res.statusText);
      return await res.arrayBuffer();
    } catch (error) {
      throw new Error("Could not load the saved stamp paper. Please upload it again.");
    }
  };

  // Open the stamp paper in a new tab. The stored link is served as a
  // download, so view it from a local PDF blob instead.
  const handleOpenStampPaper = async () => {
    const tab = window.open("", "_blank");
    try {
      const bytes = await getStampPaperBytes();
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      if (tab) tab.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      tab?.close();
      errorToast(error?.message || "Could not open the stamp paper.");
    }
  };

  const canBuildMerged = (stampPaperFile || stampPaperLink) && docOrder.length > 0;

  // Stamp paper pages first, then the selected documents in sequence. Also
  // returns, for each part, the 1-based page numbers it occupies in the merged
  // PDF and who signs it — the e-sign request uses this to place each signer.
  const buildMergedPdf = async () => {
    const mergedPdf = await PDFDocument.create();
    const docPages = [];
    const stampPdf = await PDFDocument.load(await getStampPaperBytes());
    const stampPages = await mergedPdf.copyPages(stampPdf, stampPdf.getPageIndices());
    stampPages.forEach((page) => mergedPdf.addPage(page));
    docPages.push({
      key: "stampPaper",
      pages: stampPages.map((_, i) => i + 1),
      signers: ["buyer", "rh"],
    });
    const docComponentMap = {
      dpn:              <DPNDocumentPDF formData={formData} />,
      mtudc:            <MTUDCDocumentPDF formData={formData} />,
      tfudc:            <TFUDCDocumentPDF formData={formData} />,
      sigVerify:        <SignatureVerificationDocumentPDF formData={formData} />,
      mtAppForm:        <MTApplicationFormPDF formData={formData} limitSettingData={limitSettingData} />,
      mtSanctionLetter: <MTSanctionLetterPDF formData={formData} />,
      gstDeclaration:   <GSTDeclarationDocumentPDF formData={formData} />,
      aadharConsent:    <AadharConcentandMTBLAggrementDocumentPDF formData={formData} part="consent" />,
      mtblAgreement:    <AadharConcentandMTBLAggrementDocumentPDF formData={formData} part="mtbl" />,
    };
    // Iterate in the exact user-defined order
    for (const key of docOrder) {
      const component = docComponentMap[key];
      if (!component) continue;
      const blob = await pdf(component).toBlob();
      const arrayBuffer = await blob.arrayBuffer();
      const srcPdf = await PDFDocument.load(arrayBuffer);
      const pages = await mergedPdf.copyPages(srcPdf, srcPdf.getPageIndices());
      const firstPage = mergedPdf.getPageCount() + 1;
      pages.forEach((page) => mergedPdf.addPage(page));
      docPages.push({
        key,
        pages: pages.map((_, i) => firstPage + i),
        signers: needsRhSign(key) ? ["buyer", "rh"] : ["buyer"],
      });
    }
    const mergedBytes = await mergedPdf.save();
    return { mergedBytes, docPages };
  };

  const mergedPdfBlob = async () => {
    const { mergedBytes } = await buildMergedPdf();
    return new Blob([mergedBytes], { type: "application/pdf" });
  };

  const handleViewMergedPDF = async () => {
    if (!canBuildMerged) {
      warningToast("Upload the stamp paper and add at least one document first.");
      return;
    }
    // Open the tab before the async merge so the popup isn't blocked.
    const tab = window.open("", "_blank");
    try {
      setMergingPdf(true);
      const url = URL.createObjectURL(await mergedPdfBlob());
      if (tab) tab.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setMergedViewed(true);
    } catch (error) {
      tab?.close();
      console.error("Error merging PDFs:", error);
      errorToast(error?.message || "Failed to merge documents. Please try again.");
    } finally {
      setMergingPdf(false);
    }
  };

  const handleDownloadMergedPDF = async () => {
    if (!canBuildMerged) {
      warningToast("Upload the stamp paper and add at least one document first.");
      return;
    }
    try {
      setMergingPdf(true);
      const url = URL.createObjectURL(await mergedPdfBlob());
      const link = document.createElement("a");
      link.href = url;
      link.download = `Limit_Setting_Documents_${dealerName}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      setMergedViewed(true);
    } catch (error) {
      console.error("Error merging PDFs:", error);
      errorToast(error?.message || "Failed to merge documents. Please try again.");
    } finally {
      setMergingPdf(false);
    }
  };

  // E-sign state of this case's merged documents (from get-ta-case-documents).
  const esign = limitSettingData?.limitSettingEsign || {};

  const esignReady =
    canBuildMerged &&
    mergedViewed &&
    esignConsent &&
    buyerAadharName.trim() &&
    stampPaperExpiry &&
    !mergingPdf &&
    !sendingEsign;
  // What still blocks "Send for E-Sign" — shown in the step 2 footer.
  const esignMissing = [
    !(stampPaperFile || stampPaperLink) && "stamp paper",
    !stampPaperExpiry && "stamp paper expiry",
    !mergedViewed && "review merged PDF",
    !buyerAadharName.trim() && "buyer name",    !esignConsent && "consent",
  ].filter(Boolean);

  // Merge (stamp paper first), upload and send to Signzy: the Buyer signs every
  // page first, then the RH signs the stamp paper + documents ticked for RH.
  const handleSendForEsign = async () => {
    if (!esignReady) return;
    const confirm = await Swal.fire({
      title: esign.requestSent ? "Resend for e-sign?" : "Send for e-sign?",
      text:
        `The Buyer (${buyerAadharName.trim()}) signs first, then RH (${risk_head_role}).` +
        (esign.signed
          ? " These documents are already e-signed. Sending again starts a new e-sign, and the new signed document will replace the current one."
          : esign.requestSent
            ? " The earlier signing request will be replaced."
            : ""),
      icon: esign.signed ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: esign.requestSent ? "Yes, resend" : "Yes, send",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    try {
      setSendingEsign(true);
      const { mergedBytes, docPages } = await buildMergedPdf();
      const fd = new FormData();
      fd.append("taOnboardingCaseId", data?.taOnboardingCaseId);
      fd.append("buyerAadharName", buyerAadharName.trim());      fd.append("stampPaperExpiry", stampPaperExpiry);
      fd.append("docPages", JSON.stringify(docPages));
      fd.append(
        "file",
        new File([mergedBytes], `Limit_Setting_Documents_${dealerName}.pdf`, {
          type: "application/pdf",
        })
      );
      const res = await taService.SendLimitSettingEsign(fd);
      if (res?.status === 200) {
        setLimitSettingData((prev) => ({
          ...prev,
          limitSettingEsign: res.esign,
          limitSettingMergedDocument: res.mergedDocument,
        }));
        successToast(res.msg || "Documents sent for e-sign.");
        setShowMergeModal(false);
        resetMergeSelection();
      } else {
        errorToast(res?.msg || "Failed to send for e-sign.");
      }
    } catch (error) {
      console.error("Error sending for e-sign:", error);
      errorToast(error?.message || "Failed to send for e-sign.");
    } finally {
      setSendingEsign(false);
    }
  };

  const copySigningLink = (url, who) => {
    navigator.clipboard
      ?.writeText(url)
      .then(() => successToast(`${who} signing link copied`))
      .catch(() => errorToast("Could not copy the link"));
  };

  const handleUpload = async (e, name) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileURL = URL.createObjectURL(file);
    const fileType = file.type;
    setButtonLoader(true);

    let filePreviewHTML = "";
    if (fileType === "application/pdf") {
      filePreviewHTML = `<iframe src="${fileURL}" width="100%" height="400px" style="border:1px solid #ccc;"></iframe>`;
    } else if (fileType.startsWith("image/")) {
      filePreviewHTML = `<img src="${fileURL}" style="max-width:100%; max-height:400px; border:1px solid #ccc;" />`;
    } else if (
      file.name.endsWith(".doc") ||
      file.name.endsWith(".docx") ||
      fileType === "application/msword" ||
      fileType ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      filePreviewHTML = `<p><a href="${fileURL}" target="_blank">Click here to preview the DOC file</a></p>`;
    } else {
      filePreviewHTML = `<p>File preview not supported. Click "Upload" to continue.</p>`;
    }

    try {
      const result = await Swal.fire({
        title: "Confirm Upload",
        html: `
          <p>You are about to upload: <strong>${file.name}</strong></p>
          ${filePreviewHTML}
        `,
        showCancelButton: true,
        confirmButtonText: "Yes, upload it",
        cancelButtonText: "Cancel",
        width: 600,
        heightAuto: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showCloseButton: false,
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          const formData = new FormData();
          formData.append("taOnboardingCaseId", data?.taOnboardingCaseId);
          formData.append(name, file);
          try {
            const response = await taService.UploadDoc(formData);

            if (response?.status === 200) {
              successToast(response?.msg);
              setLimitSettingData((prev) => ({
                ...prev,
                [name]: {
                  ...prev[name],
                  isUploaded: true,
                  documentPath: response?.link,
                  documentName: name || "Unknown Document",
                },
              }));
            } else {
              Swal.showValidationMessage(response?.msg || "Upload failed");
              return false;
            }
          } catch (error) {
            Swal.showValidationMessage(error.message);
            throw error;
          }
        },
        allowOutsideClick: () => !Swal.isLoading(),
      });
      e.target.value = "";
    } catch (error) {
      console.error("Error deleting partner:", error);
      errorToast("An error occurred while deleting the partner.");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoader(true);
        const formData = new FormData();
        formData.append("taOnboardingCaseId", data?.taOnboardingCaseId);
        const response = await taService.GetLimitSettingData(formData);
        setLoader(false);
        if (response?.status === 200) {
          setLimitSettingData(response?.data);
          const partnerDetails =
            data?.businessType === "partnership" &&
            response?.data?.partners?.length > 0
              ? response.data.partners.map((name) => ({
                  name,
                  pan_no: "",
                  age: "",
                  fathersName: "",
                  address: "",
                }))
              : undefined;
          setFormData((prev) => ({
            ...prev,
            otherDetails: response?.data,
            ...(partnerDetails &&
              !prev.partnerDetails && { partnerDetails }),
          }));
        }
      } catch (error) {
        errorToast(error?.msg || "Something went wrong");
        setLoader(false);
      }
    };

    fetchData();
  }, []);

  // Auto-save form data to local storage with debouncing
  useEffect(() => {
    // Don't save until initial data is loaded
    if (loader || !data?.taOnboardingCaseId) return;

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for debounced save (500ms)
    autoSaveTimeoutRef.current = setTimeout(() => {
      const success = saveFormDataToLocalStorage(
        data.taOnboardingCaseId,
        formData
      );
      
      if (!success) {
        // Show warning only once per session
        if (!sessionStorage.getItem('localStorageWarningShown')) {
          warningToast("Unable to save draft - local storage might be full");
          sessionStorage.setItem('localStorageWarningShown', 'true');
        }
      }
    }, 500);

    // Cleanup timeout on unmount
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [formData, loader, data?.taOnboardingCaseId]);

  const handleUploadDPNDocument = async (e, name) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileURL = URL.createObjectURL(file);
    const fileType = file.type;
    setButtonLoader(true);

    let filePreviewHTML = "";
    if (fileType === "application/pdf") {
      filePreviewHTML = `<iframe src="${fileURL}" width="100%" height="400px" style="border:1px solid #ccc;"></iframe>`;
    } else if (fileType.startsWith("image/")) {
      filePreviewHTML = `<img src="${fileURL}" style="max-width:100%; max-height:400px; border:1px solid #ccc;" />`;
    } else if (
      file.name.endsWith(".doc") ||
      file.name.endsWith(".docx") ||
      fileType === "application/msword" ||
      fileType ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      filePreviewHTML = `<p><a href="${fileURL}" target="_blank">Click here to preview the DOC file</a></p>`;
    } else {
      filePreviewHTML = `<p>File preview not supported. Click "Upload" to continue.</p>`;
    }

    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        html: `
          <p>You are about to upload: <strong>${file.name}</strong></p>
          ${filePreviewHTML}
        `,
        showCancelButton: true,
        confirmButtonText: "Yes, upload it",
        cancelButtonText: "Cancel",
        width: 600,
        heightAuto: false,
        allowOutsideClick: false,
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          const formData = new FormData();
          formData.append("taOnboardingCaseId", data?.taOnboardingCaseId);
          formData.append(name, file);

          try {
            const response = await taService.UploadDoc(formData);
            if (response.status === 200) {
              successToast(response?.msg);
              setLimitSettingData((prev) => ({
                ...prev,
                [name]: {
                  ...prev[name],
                  isUploaded: true,
                  documentPath: response?.link,
                  documentName: name || "Unknown Document",
                },
              }));
              return true;
            } else {
              Swal.showValidationMessage(response?.msg || "Upload failed");
              return false;
            }
          } catch (error) {
            Swal.showValidationMessage(error?.message || "Upload failed");
            return false;
          }
        },
      });

      // If cancelled
      if (!result.isConfirmed) {
        e.target.value = null;
        URL.revokeObjectURL(fileURL);
      }
    } catch (error) {
      console.error("Swal error:", error);
      errorToast("Something went wrong.");
    } finally {
      URL.revokeObjectURL(fileURL);
      setButtonLoader(false);
    }
  };

  const handleSubmitLimitSettingDoc = async () => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Yes, Mark limit setting documents upload process as completed!",
        // text: "Yes, Mark as Completed!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, submit it",
        cancelButtonText: "Cancel",
        width: 500,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          try {
            const form_Data = new FormData();
            form_Data.append("taOnboardingCaseId", data?.taOnboardingCaseId);

            const response = await taService.LimitSettingOfflineDoc(form_Data);

            if (response?.status === 200) {
              // Clear draft from local storage on successful submission
              clearFormDataFromLocalStorage(data?.taOnboardingCaseId);
              successToast(response?.msg);
              history.push(
                `/business_analyst:${user?.phone}&state=${user?.state}`
              );
            } else {
              errorToast(response?.msg);
            }
          } catch (err) {
            Swal.showValidationMessage(err.message || "Something went wrong.");
            return false;
          }
        },
      });

      if (result.isConfirmed && result.value) {
        successToast(result.value);
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      errorToast("An unexpected error occurred.");
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        successToast("Link copied to clipboard");
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
        errorToast(err?.msg);
      });
  };

  function formatCurrencyText(input) {
    return input
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize each word
      .replace(/\bLakh\b/, "Lakhs"); // Optionally convert "Lakh" to "Lakhs"
  }

  const formattedValue = formatCurrencyText(formData?.amount_in_word);

  const breadcrumbItems = [
    { name: "Home", link: "/dashboard/trade_advance/onboarding" },
    {
      name: "Limit Setting Documents",
      link: "/dashboard/trade_advance/onboarding/limit_setting_document_collection",
    },
  ];
  return (
    <>
      {loader ? (
        <div className="p-3">
          <Skeleton height={25} width={200} />
          <Skeleton count={4} height={20} className="my-1" />
        </div>
      ) : (
        <>
          {user.role === "Auction Representative" ? (
            <>
              {" "}
              <AuctionERPBreadcrumb items={breadcrumbItems} />
            </>
          ) : (
            <div className="container mt-3">
              <div className="d-flex justify-content-between">
                <i
                  className="bi bi-arrow-left-circle"
                  onClick={() => history.goBack()}
                  style={{
                    fontSize: "xx-large",
                    color: "black",
                    borderRadius: "50%",
                    cursor: "pointer",
                  }}
                ></i>
              </div>
            </div>
          )}
          <div className="container bg-glass shadow-sm p-3 mb-2 border mt-3 rounded">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <h5 className="mb-0">Limit Setting Documents</h5>
              <button
                className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                onClick={openMergeModal}
              >
                <i className="bi bi-file-earmark-zip"></i>
                <span className="d-none d-sm-inline">Merge Documents</span>
                <span className="d-inline d-sm-none">Merge Documents</span>
              </button>
            </div>

            {/* Limit setting e-sign status */}
            {esign.requestSent && (
              <div
                className={`alert ${
                  esign.signed ? "alert-success" : "alert-warning"
                } py-2 px-3 d-flex flex-wrap align-items-center gap-2 small mb-2`}
              >
                {esign.signed ? (
                  <>
                    <i className="bi bi-patch-check-fill"></i>
                    <strong>E-Signed</strong>
                    {esign.signedAt && <span>on {new Date(esign.signedAt).toLocaleString("en-IN")}</span>}
                    {esign.signedDocument && (
                      <a
                        href={esign.signedDocument}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-success ms-auto"
                      >
                        <i className="bi bi-eye me-1"></i>View signed PDF
                      </a>
                    )}
                  </>
                ) : (
                  <>
                    <i className="bi bi-hourglass-split"></i>
                    <strong>E-Sign sent</strong>
                    <span>&middot; awaiting signatures (Buyer first, then RH ({risk_head_role}))</span>
                    <span className="ms-auto d-flex gap-2">
                      {esign.buyerSigningLink && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => copySigningLink(esign.buyerSigningLink, "Buyer")}
                        >
                          <i className="bi bi-clipboard me-1"></i>Copy Buyer link
                        </button>
                      )}
                      {esign.rhSigningLink && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => copySigningLink(esign.rhSigningLink, "RH")}
                        >
                          <i className="bi bi-clipboard me-1"></i>Copy RH link
                        </button>
                      )}
                    </span>
                  </>
                )}
              </div>
            )}
            
            {/* Draft Restoration Banner */}
            {isDraftLoaded && draftTimestamp && (
              <div className="alert alert-info alert-dismissible fade show d-flex align-items-center justify-content-between" role="alert">
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle-fill me-2"></i>
                  <span>
                    <strong>Draft restored</strong> from {formatDraftTimestamp(draftTimestamp)}. 
                    Your unsaved changes have been recovered.
                  </span>
                </div>
                <div className="d-flex align-items-center">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary me-2"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to clear the saved draft and reset to original data?')) {
                        clearDraft();
                      }
                    }}
                  >
                    <i className="bi bi-trash me-1"></i>
                    Clear Draft
                  </button>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setIsDraftLoaded(false)}
                    aria-label="Close"
                  ></button>
                </div>
              </div>
            )}
            
            <div className="d-flex flex-wrap justify-content-between bg-glass rounded-2 p-1">
              {data?.companyName && (
                <div className="row">
                  <div>Company Name</div>
                  <div>
                    <b>{data?.companyName}</b>
                  </div>
                </div>
              )}
              <div className="row ms-2">
                <div>Dealer Name</div>
                <div>
                  <b>{data?.personName}</b>
                </div>
              </div>
              <div className="row text-md-center">
                <div>LOS Application Id</div>
                <div className="">
                  <b>{data?.losApplicationId}</b>
                </div>
              </div>{" "}
              <div className="row text-md-end me-2">
                <div>Credit Limit</div>
                <div>
                  <b>{indianCurrencyFormater(formData?.creditLimit)}</b>
                </div>
              </div>
            </div>
            <h6 className="mt-3 mb-0 fw-semibold">Documents</h6>
            <div className="mt-2">
                <div className="accordion" id="accordionExample">
                  {/* Aadhar Consent Section */}
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button ${openDoc === "collapseAadharConsent" ? "" : "collapsed"}`}
                        type="button"
                        onClick={() => toggleDoc("collapseAadharConsent")}
                        aria-expanded={openDoc === "collapseAadharConsent"}
                        aria-controls="collapseAadharConsent"
                      >
                        <i className="bi bi-file-earmark-richtext me-1"></i> Aadhar
                        Consent
                        {limitSettingData["aadhar_consent"]?.isUploaded && (
                          <i
                            className="bi bi-check-lg text-end ms-4 fs-5 fw-bold"
                            style={{ color: "rgb(4,147,4)" }}
                          ></i>
                        )}
                      </button>
                    </h2>
                    <div
                      id="collapseAadharConsent"
                      className={`accordion-collapse collapse ${openDoc === "collapseAadharConsent" ? "show" : ""}`}
                    >
                      <div className="accordion-body">
                        <div
                          className="p-3 mb-2 border rounded"
                          style={{ backgroundColor: "rgb(239, 246, 255)" }}
                        >
                          <h5 className="text-primary">Dealer Information</h5>
                          <hr />
                          {/* Same fields as MTBL Agreement: editing here updates both documents. */}
                          <div className="row">
                            <div className="col-12 col-md-6 mt-2">
                              <label>Name as per Aadhar</label>
                              <input
                                className="form-control"
                                type="text"
                                name="name"
                                value={formData?.name}
                                placeholder="Name As Per Aadhar"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-12 col-md-6 mt-2">
                              <label>Company Name</label>
                              <input
                                className="form-control"
                                type="text"
                                name="companyName"
                                value={formData?.companyName}
                                placeholder="Company Name"
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap justify-content-between gap-2 mt-3">
                          <div className="btn-group">
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handlePreviewAadharConsent}
                            >
                              <i className="bi bi-eye me-2"></i>
                              Preview
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handleDownloadPDFAadharConsent}
                            >
                              <i className="bi bi-download me-2"></i>
                              Download
                            </button>
                          </div>
                          <label htmlFor="aadhar_consent" className="btn btn-success">
                            <i className="bi bi-upload me-2"></i>
                            Upload Document
                            <input
                              type="file"
                              id="aadhar_consent"
                              name="aadhar_consent"
                              style={{ display: "none" }}
                              onChange={(e) =>
                                handleUploadDPNDocument(e, "aadhar_consent")
                              }
                            />
                          </label>
                        </div>
                        {limitSettingData["aadhar_consent"]?.documentPath && (
                          <div
                            className="shadow-sm p-2 mb-2 border mt-3 rounded"
                            style={{ backgroundColor: "rgb(239,246,255)" }}
                          >
                            <div className="d-flex flex-wrap justify-content-between col-12 mt-2">
                              <strong style={{ color: "rgb(58,101,255)" }}>
                                <i className="bi bi-file-earmark me-2"></i>
                                Aadhar Consent:
                              </strong>{" "}
                              <span className="text-md-end">
                                <ViewButton
                                  file={limitSettingData["aadhar_consent"].documentPath}
                                />{" "}
                                |{" "}
                                <DownloadButton
                                  file={limitSettingData["aadhar_consent"].documentPath}
                                />
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button ${openDoc === "collapseOne" ? "" : "collapsed"}`}
                        type="button"
                        onClick={() => toggleDoc("collapseOne")}
                        aria-expanded={openDoc === "collapseOne"}
                        aria-controls="collapseOne"
                      >
                        <i class="bi bi-file-earmark-richtext me-1"></i>Demand
                        Promissory Note (DPN)
                        {limitSettingData["dpn_doc"]?.isUploaded && (
                          <i
                            class="bi bi-check-lg text-end ms-4 fs-5 fw-bold"
                            style={{ color: "rgb(4,147,4)" }}
                          ></i>
                        )}
                      </button>
                    </h2>
                    <div
                      id="collapseOne"
                      className={`accordion-collapse collapse ${openDoc === "collapseOne" ? "show" : ""}`}
                    >
                      <div className="accordion-body">
                        <div
                          className="p-3 border rounded"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <div className="row">
                            {data?.businessType === "individual" ? (
                              <div className="col-md-12 mb-3">
                                <label className="form-label">
                                  Person Name
                                </label>
                                <input
                                  className="form-control"
                                  type="text"
                                  name="personName"
                                  value={formData?.personName}
                                  placeholder="Person Name"
                                  onChange={handleChange}
                                />
                              </div>
                            ) : (
                              <>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Dealer Name
                                  </label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="dealerName"
                                    value={
                                      formData?.dealerName !== undefined
                                        ? formData?.dealerName
                                        : formData?.personName || ""
                                    }
                                    placeholder="Dealer Name"
                                    onChange={handleChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Company Name
                                  </label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="companyName"
                                    value={formData?.companyName}
                                    placeholder="Company Name"
                                    onChange={handleChange}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Amount</label>
                              <input
                                type="text"
                                className="form-control"
                                name="creditLimit"
                                value={indianCurrencyFormater(
                                  formData.creditLimit,
                                  0
                                )}
                                placeholder="TA Credit Limit"
                                onChange={(e) => {
                                  const value = currencyToNumber(
                                    e.target.value
                                  );
                                  setFormData((prev) => ({
                                    ...prev,
                                    creditLimit: value,
                                    amount_in_word: rupeesToWords(value),
                                  }));
                                }}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">
                                Amount In Words
                              </label>
                              <input
                                className="form-control"
                                type="text"
                                name="amount_in_word"
                                value={formData?.amount_in_word}
                                placeholder="Amount in Words"
                                onChange={handleChange}
                                disabled
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Date</label>
                              <input
                                className="form-control"
                                type="date"
                                name="date"
                                value={formData?.date}
                                placeholder="Date"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Pan No</label>
                              <input
                                className="form-control"
                                type="text"
                                name="pan_no"
                                value={formData?.pan_no.toUpperCase()}
                                placeholder="Pan No."
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    pan_no: e.target.value.toUpperCase(),
                                  }))
                                }
                                onWheel={(e) => e.target.blur()}
                                maxLength={10}
                                pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                                title="PAN should be in format: 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)"
                              />
                            </div>
                            <div className="col-md-12 mb-3">
                              <label className="form-label">Address</label>
                              <input
                                className="form-control"
                                type="text"
                                name="address"
                                value={formData?.address}
                                placeholder="Address"
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="d-flex flex-wrap justify-content-between gap-2 mt-3">
                          <div className="btn-group">
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handlePreviewPDF}
                            >
                              <i className="bi bi-eye me-2"></i>
                              Preview
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handleDownloadPDF}
                            >
                              <i className="bi bi-download me-2"></i>
                              Download
                            </button>
                          </div>
                          <label htmlFor="dpn_doc" className="btn btn-success">
                            <i className="bi bi-upload me-2"></i>
                            Upload Document
                            <input
                              type="file"
                              id="dpn_doc"
                              name="dpn_doc"
                              style={{ display: "none" }}
                              onChange={(e) =>
                                handleUploadDPNDocument(e, "dpn_doc")
                              }
                            />
                          </label>
                        </div>
                        {limitSettingData["dpn_doc"]?.isUploaded && (
                          <div className="mt-3 alert alert-success d-flex justify-content-between align-items-center">
                            <div>
                              <i className="bi bi-file-earmark-check me-2"></i>
                              <strong>
                                {limitSettingData["dpn_doc"].documentName ===
                                  "dpn_doc" && "DPN Document: Uploaded"}
                              </strong>
                            </div>
                            <div>
                              <ViewButton
                                file={limitSettingData["dpn_doc"].documentPath}
                              />
                              <span className="mx-1">|</span>
                              <DownloadButton
                                file={limitSettingData["dpn_doc"].documentPath}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* GST Declaration Section */}
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button ${openDoc === "collapseGSTDeclaration" ? "" : "collapsed"}`}
                        type="button"
                        onClick={() => toggleDoc("collapseGSTDeclaration")}
                        aria-expanded={openDoc === "collapseGSTDeclaration"}
                        aria-controls="collapseGSTDeclaration"
                      >
                        <i className="bi bi-file-earmark-richtext me-1"></i>
                        GST Declaration
                        {limitSettingData["gst_declaration"]?.isUploaded && (
                          <i
                            className="bi bi-check-lg text-end ms-4 fs-5 fw-bold"
                            style={{ color: "rgb(4,147,4)" }}
                          ></i>
                        )}
                      </button>
                    </h2>
                    <div
                      id="collapseGSTDeclaration"
                      className={`accordion-collapse collapse ${openDoc === "collapseGSTDeclaration" ? "show" : ""}`}
                    >
                      <div className="accordion-body">
                        <div
                          className="p-3 border rounded"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">
                                Name as per DPN
                              </label>
                              <input
                                className="form-control"
                                type="text"
                                name="gst_declaration_name"
                                value={
                                  formData?.gst_declaration_name !== undefined
                                    ? formData?.gst_declaration_name
                                    : formData?.personName ||
                                      formData?.companyName ||
                                      ""
                                }
                                placeholder="Name as per DPN"
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap justify-content-between gap-2 mt-3">
                          <div className="btn-group">
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handlePreviewPDFForGSTDeclaration}
                            >
                              <i className="bi bi-eye me-2"></i>
                              Preview
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handleDownloadPDFGSTDeclaration}
                            >
                              <i className="bi bi-download me-2"></i>
                              Download
                            </button>
                          </div>
                          <label
                            htmlFor={"gst_declaration"}
                            className="btn btn-success"
                          >
                            <i className="bi bi-upload me-2"></i>
                            Upload GST Declaration
                            <input
                              type="file"
                              id={"gst_declaration"}
                              name={"gst_declaration"}
                              style={{ display: "none" }}
                              onChange={(e) =>
                                handleUpload(e, "gst_declaration")
                              }
                            />
                          </label>
                        </div>

                        {limitSettingData["gst_declaration"]?.isUploaded && (
                          <div className="mt-3 alert alert-success d-flex justify-content-between align-items-center">
                            <div>
                              <i className="bi bi-file-earmark-check me-2"></i>
                              <strong>
                                {limitSettingData["gst_declaration"]
                                  .documentName === "gst_declaration" &&
                                  "GST Declaration Document: Uploaded"}
                              </strong>
                            </div>
                            <div>
                              <ViewButton
                                file={
                                  limitSettingData["gst_declaration"]
                                    .documentPath
                                }
                              />
                              <span className="mx-1">|</span>
                              <DownloadButton
                                file={
                                  limitSettingData["gst_declaration"]
                                    .documentPath
                                }
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* MT Application Form & MT Sanction Letter Sections - Only show when TA funding by MT */}
                  {formData?.is_ta_funding_by_mt && (
                  <>
                  {/* MT Application Form Section */}
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button ${openDoc === "collapseMTApplicationForm" ? "" : "collapsed"}`}
                        type="button"
                        onClick={() => toggleDoc("collapseMTApplicationForm")}
                        aria-expanded={openDoc === "collapseMTApplicationForm"}
                        aria-controls="collapseMTApplicationForm"
                      >
                        <i className="bi bi-file-earmark-text me-1"></i>
                        MT Application Form
                        {limitSettingData["mt_application_form"]?.isUploaded && (
                          <i
                            className="bi bi-check-lg text-end ms-4 fs-5 fw-bold"
                            style={{ color: "rgb(4,147,4)" }}
                          ></i>
                        )}
                      </button>
                    </h2>
                    <div
                      id="collapseMTApplicationForm"
                      className={`accordion-collapse collapse ${openDoc === "collapseMTApplicationForm" ? "show" : ""}`}
                    >
                      <div className="accordion-body">
                        <div
                          className="p-3 border rounded"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Dealer Name</label>
                              <input
                                className="form-control"
                                type="text"
                                name="personName"
                                value={formData?.personName}
                                placeholder="Enter Dealer Name"
                                onChange={handleChange}
                              />
                            </div>
                            {data?.businessType !== "individual" && (
                              <div className="col-md-6 mb-3">
                                <label className="form-label">Company Name</label>
                                <input
                                  className="form-control"
                                  type="text"
                                  name="companyName"
                                  value={formData?.companyName}
                                  placeholder="Enter Company Name"
                                  onChange={handleChange}
                                />
                              </div>
                            )}
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Registered Address</label>
                              <input
                                className="form-control"
                                type="text"
                                name="address"
                                value={formData?.address}
                                placeholder="Enter Registered Address"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Contact No</label>
                              <input
                                className="form-control"
                                type="text"
                                name="personPhone"
                                value={formData?.personPhone}
                                placeholder="Enter Contact Number"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Email ID</label>
                              <input
                                className="form-control"
                                type="email"
                                name="email"
                                value={formData?.email}
                                placeholder="Enter Email ID"
                                onChange={handleChange}
                              />
                            </div>
                            {/* Passport Photo Upload */}
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Passport Size Photo</label>
                              <div className="d-flex align-items-start gap-3">
                                <div className="flex-grow-1">
                                  <input
                                    className="form-control"
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (!file) return;
                                      // Resize to passport-photo dimensions and export as JPEG.
                                      // Canvas normalises any input format (HEIC, WebP, PNG, etc.)
                                      // to a standard JPEG data URL that @react-pdf/renderer
                                      // embeds correctly in all browsers and PDF viewers.
                                      const objectUrl = URL.createObjectURL(file);
                                      const img = new Image();
                                      img.onload = () => {
                                        // Passport photo container: 85pt × 105pt at 2× (170×210px)
                                        const W = 170;
                                        const H = 210;
                                        const canvas = document.createElement("canvas");
                                        canvas.width = W;
                                        canvas.height = H;
                                        const ctx = canvas.getContext("2d");
                                        // Fill white background (avoids transparent PNG artefacts)
                                        ctx.fillStyle = "#ffffff";
                                        ctx.fillRect(0, 0, W, H);
                                        // Scale with cover-crop to fill the box
                                        const imgAspect = img.width / img.height;
                                        const boxAspect = W / H;
                                        let sx = 0, sy = 0, sw = img.width, sh = img.height;
                                        if (imgAspect > boxAspect) {
                                          sw = img.height * boxAspect;
                                          sx = (img.width - sw) / 2;
                                        } else {
                                          sh = img.width / boxAspect;
                                          sy = (img.height - sh) / 2;
                                        }
                                        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, W, H);
                                        URL.revokeObjectURL(objectUrl);
                                        setLimitSettingData((prev) => ({
                                          ...prev,
                                          passportPhoto: canvas.toDataURL("image/jpeg", 0.85),
                                        }));
                                      };
                                      img.onerror = () => URL.revokeObjectURL(objectUrl);
                                      img.src = objectUrl;
                                    }}
                                  />
                                  <small className="text-muted">Upload a passport size photo (JPG, PNG)</small>
                                </div>
                                {limitSettingData?.passportPhoto && (
                                  <div
                                    style={{
                                      width: "60px",
                                      height: "75px",
                                      border: "1px solid #dee2e6",
                                      borderRadius: "4px",
                                      overflow: "hidden",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <img
                                      src={limitSettingData.passportPhoto}
                                      alt="Passport Preview"
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="d-flex flex-wrap justify-content-between gap-2 mt-3">
                          <div className="btn-group">
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handlePreviewMTApplicationForm}
                            >
                              <i className="bi bi-eye me-2"></i>
                              Preview
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handleDownloadMTApplicationForm}
                            >
                              <i className="bi bi-download me-2"></i>
                              Download
                            </button>
                          </div>
                          <label htmlFor="mt_application_form" className="btn btn-success">
                            <i className="bi bi-upload me-2"></i>
                            Upload Document
                            <input
                              type="file"
                              id="mt_application_form"
                              name="mt_application_form"
                              style={{ display: "none" }}
                              onChange={(e) =>
                                handleUploadDPNDocument(e, "mt_application_form")
                              }
                            />
                          </label>
                        </div>
                        {limitSettingData["mt_application_form"]?.isUploaded && (
                          <div className="mt-3 alert alert-success d-flex justify-content-between align-items-center">
                            <div>
                              <i className="bi bi-file-earmark-check me-2"></i>
                              <strong>
                                {limitSettingData["mt_application_form"].documentName ===
                                  "mt_application_form" && "MT Application Form: Uploaded"}
                              </strong>
                            </div>
                            <div>
                              <ViewButton
                                file={limitSettingData["mt_application_form"].documentPath}
                              />
                              <span className="mx-1">|</span>
                              <DownloadButton
                                file={limitSettingData["mt_application_form"].documentPath}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* MT Sanction Letter Section */}
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button ${openDoc === "collapseMTSanctionLetter" ? "" : "collapsed"}`}
                        type="button"
                        onClick={() => toggleDoc("collapseMTSanctionLetter")}
                        aria-expanded={openDoc === "collapseMTSanctionLetter"}
                        aria-controls="collapseMTSanctionLetter"
                      >
                        <i className="bi bi-file-earmark-text me-1"></i>
                        MT Sanction Letter
                        {limitSettingData["mt_sanction_letter"]?.isUploaded && (
                          <i
                            className="bi bi-check-lg text-end ms-4 fs-5 fw-bold"
                            style={{ color: "rgb(4,147,4)" }}
                          ></i>
                        )}
                      </button>
                    </h2>
                    <div
                      id="collapseMTSanctionLetter"
                      className={`accordion-collapse collapse ${openDoc === "collapseMTSanctionLetter" ? "show" : ""}`}
                    >
                      <div className="accordion-body">
                        <div
                          className="p-3 border rounded"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Reference Number</label>
                              <div className="d-flex gap-2 align-items-start flex-column flex-sm-row">
                                {!limitSettingData?.isTAFundingByMTReferenceNumberGenerated && !limitSettingData?.taFundingByMTReferenceNumber ? (
                                  <button
                                    type="button"
                                    className="btn btn-primary w-100 w-sm-auto"
                                    onClick={handleGenerateReferenceNumber}
                                    disabled={generatingRefNumber}
                                  >
                                    {generatingRefNumber ? (
                                      <>
                                        <span
                                          className="spinner-border spinner-border-sm me-2"
                                          role="status"
                                          aria-hidden="true"
                                        ></span>
                                        Generating...
                                      </>
                                    ) : (
                                      <>
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Generate Reference Number
                                      </>
                                    )}
                                  </button>
                                ) : (
                                  <div className="w-100">
                                    <div
                                      className="alert alert-success mb-0 d-flex align-items-center"
                                      style={{ padding: "0.75rem 1rem" }}
                                    >
                                      <div className="d-flex align-items-center">
                                        <i className="bi bi-check-circle-fill me-2"></i>
                                        <strong>{limitSettingData?.taFundingByMTReferenceNumber || formData?.referenceNumber}</strong>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Date</label>
                              <input
                                className="form-control"
                                type="date"
                                name="date"
                                value={formData?.date}
                                placeholder="Date"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Name</label>
                              <input
                                className="form-control"
                                type="text"
                                name="personName"
                                value={formData?.personName}
                                placeholder="Enter Name"
                                onChange={handleChange}
                              />
                            </div>
                            {data?.businessType !== "individual" && (
                              <div className="col-md-6 mb-3">
                                <label className="form-label">Company Name</label>
                                <input
                                  className="form-control"
                                  type="text"
                                  name="companyName"
                                  value={formData?.companyName}
                                  placeholder="Enter Company Name"
                                  onChange={handleChange}
                                />
                              </div>
                            )}
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Address</label>
                              <input
                                className="form-control"
                                type="text"
                                name="address"
                                value={formData?.address}
                                placeholder="Enter Address"
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="d-flex flex-wrap justify-content-between gap-2 mt-3">
                          <div className="btn-group">
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handlePreviewMTSanctionLetter}
                            >
                              <i className="bi bi-eye me-2"></i>
                              Preview
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handleDownloadMTSanctionLetter}
                            >
                              <i className="bi bi-download me-2"></i>
                              Download
                            </button>
                          </div>
                          <label htmlFor="mt_sanction_letter" className="btn btn-success">
                            <i className="bi bi-upload me-2"></i>
                            Upload Document
                            <input
                              type="file"
                              id="mt_sanction_letter"
                              name="mt_sanction_letter"
                              style={{ display: "none" }}
                              onChange={(e) =>
                                handleUploadDPNDocument(e, "mt_sanction_letter")
                              }
                            />
                          </label>
                        </div>
                        {limitSettingData["mt_sanction_letter"]?.isUploaded && (
                          <div className="mt-3 alert alert-success d-flex justify-content-between align-items-center">
                            <div>
                              <i className="bi bi-file-earmark-check me-2"></i>
                              <strong>
                                {limitSettingData["mt_sanction_letter"].documentName ===
                                  "mt_sanction_letter" && "MT Sanction Letter: Uploaded"}
                              </strong>
                            </div>
                            <div>
                              <ViewButton
                                file={limitSettingData["mt_sanction_letter"].documentPath}
                              />
                              <span className="mx-1">|</span>
                              <DownloadButton
                                file={limitSettingData["mt_sanction_letter"].documentPath}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  </>
                  )}

                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button ${openDoc === "collapseTwo" ? "" : "collapsed"}`}
                        type="button"
                        onClick={() => toggleDoc("collapseTwo")}
                        aria-expanded={openDoc === "collapseTwo"}
                        aria-controls="collapseTwo"
                      >
                        <i class="bi bi-file-earmark-richtext me-1"></i> MT UDC
                        {limitSettingData["mt_udc_doc"]?.isUploaded && (
                          <i
                            class="bi bi-check-lg text-end ms-4 fs-5 fw-bold"
                            style={{ color: "rgb(4,147,4)" }}
                          ></i>
                        )}
                      </button>
                    </h2>
                    <div
                      id="collapseTwo"
                      className={`accordion-collapse collapse ${openDoc === "collapseTwo" ? "show" : ""}`}
                    >
                      <div className="accordion-body">
                        <div
                          className="p-3 border rounded"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <div className="row">
                            {data?.businessType === "individual" ? (
                              <div className="col-md-6 mb-3">
                                <label className="form-label">
                                  Person Name
                                </label>
                                <input
                                  className="form-control"
                                  type="text"
                                  name="personName"
                                  value={formData?.personName}
                                  placeholder="Person Name"
                                  onChange={handleChange}
                                />
                              </div>
                            ) : (
                              <>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Dealer Name
                                  </label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="dealerName"
                                    value={
                                      formData?.dealerName !== undefined
                                        ? formData?.dealerName
                                        : formData?.personName || ""
                                    }
                                    placeholder="Dealer Name"
                                    onChange={handleChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Company Name
                                  </label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="companyName"
                                    value={formData?.companyName}
                                    placeholder="Company Name"
                                    onChange={handleChange}
                                  />
                                </div>
                              </>
                            )}
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Account Name</label>
                              <input
                                className="form-control"
                                type="text"
                                name="account_name"
                                value={formData?.account_name}
                                placeholder="Enter account name"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Account No.</label>
                              <input
                                className="form-control"
                                type="number"
                                name="account_no"
                                value={formData?.account_no}
                                placeholder="Enter account number"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Bank Name</label>
                              <input
                                className="form-control"
                                type="text"
                                name="bank_name"
                                value={formData?.bank_name}
                                placeholder="Enter bank name"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Branch</label>
                              <input
                                className="form-control"
                                type="text"
                                name="branch"
                                value={formData?.branch}
                                placeholder="Enter branch"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">
                                Cheque Number 1
                              </label>
                              <input
                                className="form-control"
                                type="number"
                                name="cheque_number"
                                value={formData?.cheque_number}
                                placeholder="Enter cheque number 1"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">
                                Cheque Number 2
                              </label>
                              <input
                                className="form-control"
                                type="number"
                                name="cheque_number_2"
                                value={formData?.cheque_number_2}
                                placeholder="Enter cheque number 2"
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap justify-content-between gap-2 mt-3">
                          <div className="btn-group">
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handlePreviewPDFForMTUDC}
                            >
                              <i className="bi bi-eye me-2"></i>
                              Preview
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handleDownloadPDFMTUDC}
                            >
                              <i className="bi bi-download me-2"></i>
                              Download
                            </button>
                          </div>
                          <label
                            htmlFor={"mt_udc_doc"}
                            className="btn btn-success"
                          >
                            <i className="bi bi-upload me-2"></i>
                            Upload Document
                            <input
                              type="file"
                              id={"mt_udc_doc"}
                              name={"mt_udc_doc"}
                              style={{ display: "none" }}
                              onChange={(e) =>
                                handleUploadDPNDocument(e, "mt_udc_doc")
                              }
                            />
                          </label>
                        </div>
                        {limitSettingData["mt_udc_doc"]?.isUploaded && (
                          <div className="mt-3 alert alert-success d-flex justify-content-between align-items-center">
                            <div>
                              <i className="bi bi-file-earmark-check me-2"></i>
                              <strong>
                                {limitSettingData["mt_udc_doc"].documentName ===
                                  "mt_udc_doc" && "MT UDC Documents: Uploaded"}
                              </strong>
                            </div>
                            <div>
                              <ViewButton
                                file={
                                  limitSettingData["mt_udc_doc"].documentPath
                                }
                              />
                              <span className="mx-1">|</span>
                              <DownloadButton
                                file={
                                  limitSettingData["mt_udc_doc"].documentPath
                                }
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* MTBL Agreement Section */}
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button ${openDoc === "collapseMTBLAgreement" ? "" : "collapsed"}`}
                        type="button"
                        onClick={() => toggleDoc("collapseMTBLAgreement")}
                        aria-expanded={openDoc === "collapseMTBLAgreement"}
                        aria-controls="collapseMTBLAgreement"
                      >
                        <i class="bi bi-file-earmark-richtext me-1"></i> MTBL
                        Agreement
                        {limitSettingData["mt_bl_agreement"]?.isUploaded && (
                          <i
                            class="bi bi-check-lg text-end ms-4 fs-5 fw-bold"
                            style={{ color: "rgb(4,147,4)" }}
                          ></i>
                        )}
                      </button>
                    </h2>
                    <div
                      id="collapseMTBLAgreement"
                      className={`accordion-collapse collapse ${openDoc === "collapseMTBLAgreement" ? "show" : ""}`}
                    >
                      <div className="accordion-body">
                        <div
                          className="p-3 mb-2 border mt-3 rounded"
                          style={{ backgroundColor: "rgb(239, 246, 255)" }}
                        >
                          <h5 className="text-primary">Dealer Information</h5>
                          <hr />
                          <div className="row">
                            <div className="col-12 mt-2">
                              <label>Name as per Aadhar</label>
                              <input
                                className="form-control"
                                type="text"
                                name="name"
                                value={formData?.name}
                                placeholder="Name As Per Aadhar"
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-12 col-md-6 mt-2">
                              <label>Dealer Id</label>
                              <input
                                className="form-control"
                                type="text"
                                name="dealerId"
                                value={formData?.otherDetails?.dealerId}
                                placeholder="dealerId (ex: - KSOS000)"
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    otherDetails: {
                                      ...prev.otherDetails,
                                      dealerId: e.target.value,
                                    },
                                  }));
                                }}
                              />
                            </div>
                            {data?.businessType === "individual" ? (
                              <div className="col-12 col-md-6 mt-2">
                                <label>Dealer Name</label>
                                <input
                                  className="form-control"
                                  type="text"
                                  name="personName"
                                  value={formData?.personName}
                                  placeholder="Dealer Name"
                                  onChange={handleChange}
                                />
                              </div>
                            ) : (
                              <div className="col-12 col-md-6 mt-2">
                                <label>Company Name</label>
                                <input
                                  className="form-control"
                                  type="text"
                                  name="companyName"
                                  value={formData?.companyName}
                                  placeholder="Date"
                                  onChange={handleChange}
                                />
                              </div>
                            )}
                          </div>
                          {formData?.businessType !== "partnership" && (
                            <div className="row">
                              <div className="col-12 col-md-6 mt-2">
                                <label>Fathers Name</label>
                                <input
                                  className="form-control"
                                  type="text"
                                  name="fathersName"
                                  value={formData?.fathersName}
                                  placeholder="Enter father's name"
                                  onChange={handleChange}
                                />
                              </div>
                              <div className="col-12 col-md-6 mt-2">
                                <label>Age of Dealer</label>
                                <input
                                  className="form-control"
                                  type="text"
                                  name="age"
                                  value={formData?.age}
                                  placeholder="Enter age of dealer"
                                  onChange={handleChange}
                                />
                              </div>
                            </div>
                          )}
                          <div className="row">
                            {formData?.businessType === "pvt_ltd" && (
                              <div className="col-12 col-md-6 mt-2">
                                <label>CIN Number</label>
                                <input
                                  className="form-control"
                                  type="text"
                                  name="cin"
                                  value={formData?.cin}
                                  placeholder="Enter CIN number"
                                  onChange={handleChange}
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        {formData?.businessType === "partnership" && (
                          <div
                            className="p-3 mb-2 border mt-3 rounded"
                            style={{ backgroundColor: "rgb(239, 246, 255)" }}
                          >
                            <h5 className="text-primary">Partner Information</h5>
                            <hr />
                            {(formData?.partnerDetails || []).map((partner, index) => (
                              <div
                                key={index}
                                className="border rounded p-3 mb-3"
                                style={{ backgroundColor: "#fff" }}
                              >
                                <h6 className="text-secondary mb-3">
                                  Partner {index + 1}
                                </h6>
                                <div className="row">
                                  <div className="col-12 col-md-6 mt-2">
                                    <label>Partner Name</label>
                                    <input
                                      className="form-control"
                                      type="text"
                                      value={partner.name}
                                      placeholder="Partner Name"
                                      onChange={(e) =>
                                        handlePartnerChange(index, "name", e.target.value)
                                      }
                                    />
                                  </div>
                                  <div className="col-12 col-md-6 mt-2">
                                    <label>PAN No</label>
                                    <input
                                      className="form-control"
                                      type="text"
                                      value={partner.pan_no}
                                      placeholder="Enter PAN number"
                                      maxLength={10}
                                      onChange={(e) =>
                                        handlePartnerChange(
                                          index,
                                          "pan_no",
                                          e.target.value.toUpperCase()
                                        )
                                      }
                                    />
                                  </div>
                                  <div className="col-12 col-md-6 mt-2">
                                    <label>Age</label>
                                    <input
                                      className="form-control"
                                      type="text"
                                      value={partner.age}
                                      placeholder="Enter age"
                                      onChange={(e) =>
                                        handlePartnerChange(index, "age", e.target.value)
                                      }
                                    />
                                  </div>
                                  <div className="col-12 col-md-6 mt-2">
                                    <label>Father Name</label>
                                    <input
                                      className="form-control"
                                      type="text"
                                      value={partner.fathersName}
                                      placeholder="Enter father's name"
                                      onChange={(e) =>
                                        handlePartnerChange(
                                          index,
                                          "fathersName",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                  <div className="col-12 mt-2">
                                    <label>Address as per Aadhar Card</label>
                                    <input
                                      className="form-control"
                                      type="text"
                                      value={partner.address}
                                      placeholder="Enter address as per Aadhar card"
                                      onChange={(e) =>
                                        handlePartnerChange(
                                          index,
                                          "address",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div
                          className="p-3 mb-2 border mt-3 rounded"
                          style={{ backgroundColor: "rgb(239, 246, 255)" }}
                        >
                          <h5 className="text-primary">Bank Details</h5>
                          <hr />
                          <div className="row">
                            <div className="col-12 col-md-6 mt-2">
                              <label>Account Name</label>
                              <input
                                className="form-control"
                                type="text"
                                name="account_name"
                                value={formData?.account_name}
                                placeholder="Enter account name"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-12 col-md-6 mt-2">
                              <label>Account No.</label>
                              <input
                                className="form-control"
                                type="number"
                                name="account_no"
                                value={formData?.account_no}
                                placeholder="Enter account number"
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-12 col-md-6 mt-2">
                              <label>Bank Name</label>
                              <input
                                className="form-control"
                                type="text"
                                name="bank_name"
                                value={formData?.bank_name}
                                placeholder="Enter bank name"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-12 col-md-6 mt-2">
                              <label>Branch</label>
                              <input
                                className="form-control"
                                type="text"
                                name="branch"
                                value={formData?.branch}
                                placeholder="Enter branch"
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-12 col-md-6 mt-2">
                              <label>IFSC</label>
                              <input
                                className="form-control"
                                type="text"
                                name="ifsc"
                                value={formData?.ifsc}
                                placeholder="Enter IFSC code"
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>
                        <div
                          className="p-3 mb-2 border mt-3 rounded"
                          style={{ backgroundColor: "rgb(239, 246, 255)" }}
                        >
                          <h5 className="text-primary">Cheque Details</h5>
                          <hr />
                          <div className="row">
                            <div className="col-12 col-md-6 mt-2">
                              <label>Cheque Number 1</label>
                              <input
                                className="form-control"
                                type="number"
                                name="cheque_number"
                                value={formData?.cheque_number}
                                placeholder="Enter cheque number 1"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-12 col-md-6 mt-2">
                              <label>Cheque Number 2</label>
                              <input
                                className="form-control"
                                type="number"
                                name="cheque_number_2"
                                value={formData?.cheque_number_2}
                                placeholder="Enter cheque number 2"
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap justify-content-between gap-2 mt-3">
                          <div className="btn-group">
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handlePreviewMTBLAgreement}
                            >
                              <i className="bi bi-eye me-2"></i>
                              Preview
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handleDownloadPDFMTBLAgreement}
                            >
                              <i className="bi bi-download me-2"></i>
                              Download
                            </button>
                          </div>
                          <label htmlFor="mt_bl_agreement" className="btn btn-success">
                            <i className="bi bi-upload me-2"></i>
                            Upload Document
                            <input
                              type="file"
                              id="mt_bl_agreement"
                              name="mt_bl_agreement"
                              style={{ display: "none" }}
                              onChange={(e) =>
                                handleUploadDPNDocument(e, "mt_bl_agreement")
                              }
                            />
                          </label>
                        </div>
                        <div className="">
                          {limitSettingData["mt_bl_agreement"]?.documentPath && (
                            <div
                              className="shadow-sm p-2 mb-2 border mt-3 rounded"
                              style={{ backgroundColor: "rgb(239,246,255)" }}
                            >
                              <div className="d-flex flex-wrap justify-content-between col-12 mt-2 ">
                                <strong
                                  className="col-md-start"
                                  style={{ color: "rgb(58,101,255)" }}
                                >
                                  <i class="bi bi-file-earmark me-2"></i>
                                  {mtblAgreementLabel(limitSettingData)}
                                  :
                                </strong>{" "}
                                <span className="text-md-end">
                                  <ViewButton
                                    file={
                                      limitSettingData["mt_bl_agreement"]
                                        .documentPath
                                    }
                                  />{" "}
                                  |{" "}
                                  <DownloadButton
                                    file={
                                      limitSettingData["mt_bl_agreement"]
                                        .documentPath
                                    }
                                  />
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="d-flex flex-wrap justify-content-between ">
                          {!limitSettingData["mt_bl_agreement"]
                            ?.is_document_signed &&
                            limitSettingData["mt_bl_agreement"]
                              ?.document_sent_for_signing &&
                            limitSettingData["mt_bl_agreement"].signing_link &&
                            Object.entries(
                              limitSettingData["mt_bl_agreement"]
                                .signing_link || {}
                            ).map(([name, link], index) => (
                              <div key={index} className="col-12 mt-2">
                                <strong>{name} Signing Link:</strong>{" "}
                                <i
                                  className="bi bi-copy"
                                  style={{
                                    cursor: "pointer",
                                    marginLeft: "8px",
                                  }}
                                  onClick={() => copyToClipboard(link)}
                                  title={`Click to copy ${name}'s signing link`}
                                />
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Signature Verification Section */}

                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button ${openDoc === "collapseSignatureVerification" ? "" : "collapsed"}`}
                        type="button"
                        onClick={() => toggleDoc("collapseSignatureVerification")}
                        aria-expanded={openDoc === "collapseSignatureVerification"}
                        aria-controls="collapseSignatureVerification"
                      >
                        <i className="bi bi-file-earmark-richtext me-1"></i>
                        Signature Verification
                        {limitSettingData["signature_verification"]
                          ?.isUploaded && (
                          <i
                            className="bi bi-check-lg text-end ms-4 fs-5 fw-bold"
                            style={{ color: "rgb(4,147,4)" }}
                          ></i>
                        )}
                      </button>
                    </h2>
                    <div
                      id="collapseSignatureVerification"
                      className={`accordion-collapse collapse ${openDoc === "collapseSignatureVerification" ? "show" : ""}`}
                    >
                      <div className="accordion-body">
                        <div
                          className="p-3 border rounded"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <div className="row">
                            {data?.businessType === "individual" ? (
                              <>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Name of the account holder
                                  </label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="sv_account_holder_name"
                                    value={
                                      formData?.sv_account_holder_name ||
                                      formData?.personName
                                    }
                                    placeholder="Dealer Name"
                                    onChange={handleChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Address of the account holder
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    name="sv_account_holder_address"
                                    value={
                                      formData?.sv_account_holder_address ||
                                      formData?.address
                                    }
                                    placeholder="Address as per DPN"
                                    onChange={handleChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Account Number
                                  </label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="sv_account_number"
                                    value={
                                      formData?.sv_account_number ||
                                      formData?.account_no
                                    }
                                    placeholder="As per UDC"
                                    onChange={handleChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Bank Name
                                  </label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="sv_bank_name"
                                    value={
                                      formData?.sv_bank_name ||
                                      formData?.bank_name
                                    }
                                    placeholder="As per UDC"
                                    onChange={handleChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Name of the Authorized Signatory
                                  </label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="sv_authorized_signatory"
                                    value={
                                      formData?.sv_authorized_signatory !==
                                      undefined
                                        ? formData?.sv_authorized_signatory
                                        : formData?.personName || ""
                                    }
                                    placeholder="Dealer Name"
                                    onChange={handleChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">Name</label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="sv_name"
                                    value={
                                      formData?.sv_name !== undefined
                                        ? formData?.sv_name
                                        : formData?.personName || ""
                                    }
                                    placeholder="Dealer Name"
                                    onChange={handleChange}
                                  />
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Name of the organization
                                  </label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="sv_organization_name"
                                    value={
                                      formData?.sv_organization_name ||
                                      formData?.companyName
                                    }
                                    placeholder="Company Name"
                                    onChange={handleChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Address of the organization
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    name="sv_organization_address"
                                    value={
                                      formData?.sv_organization_address ||
                                      formData?.address
                                    }
                                    placeholder="Address as per DPN"
                                    onChange={handleChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Account Number
                                  </label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="sv_account_number"
                                    value={
                                      formData?.sv_account_number ||
                                      formData?.account_no
                                    }
                                    placeholder="As per UDC"
                                    onChange={handleChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Bank Name
                                  </label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="sv_bank_name"
                                    value={
                                      formData?.sv_bank_name ||
                                      formData?.bank_name
                                    }
                                    placeholder="As per UDC"
                                    onChange={handleChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Name of the Authorized Signatory
                                  </label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="sv_authorized_signatory"
                                    value={
                                      formData?.sv_authorized_signatory !==
                                      undefined
                                        ? formData?.sv_authorized_signatory
                                        : formData?.personName || ""
                                    }
                                    placeholder="Dealer Name"
                                    onChange={handleChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">Name</label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="sv_name"
                                    value={
                                      formData?.sv_name !== undefined
                                        ? formData?.sv_name
                                        : formData?.personName || ""
                                    }
                                    placeholder="Dealer Name"
                                    onChange={handleChange}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="d-flex flex-wrap justify-content-between gap-2 mt-3">
                          <div className="btn-group">
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handlePreviewPDFForSignatureVerification}
                            >
                              <i className="bi bi-eye me-2"></i>
                              Preview
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handleDownloadPDFSignatureVerification}
                            >
                              <i className="bi bi-download me-2"></i>
                              Download
                            </button>
                          </div>
                          <label
                            htmlFor={"signature_verification"}
                            className="btn btn-success"
                          >
                            <i className="bi bi-upload me-2"></i>
                            Upload Signature Verification
                            <input
                              type="file"
                              id={"signature_verification"}
                              name={"signature_verification"}
                              style={{ display: "none" }}
                              onChange={(e) =>
                                handleUpload(e, "signature_verification")
                              }
                            />
                          </label>
                        </div>

                        {limitSettingData["signature_verification"]
                          ?.isUploaded && (
                          <div className="mt-3 alert alert-success d-flex justify-content-between align-items-center">
                            <div>
                              <i className="bi bi-file-earmark-check me-2"></i>
                              <strong>
                                {limitSettingData["signature_verification"]
                                  .documentName === "signature_verification" &&
                                  "Signature Verification Document: Uploaded"}
                              </strong>
                            </div>
                            <div>
                              <ViewButton
                                file={
                                  limitSettingData["signature_verification"]
                                    .documentPath
                                }
                              />
                              <span className="mx-1">|</span>
                              <DownloadButton
                                file={
                                  limitSettingData["signature_verification"]
                                    .documentPath
                                }
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* TF UDC Section - Only show when NOT TA funding by MT */}
                  {!formData?.is_ta_funding_by_mt && (
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button ${openDoc === "collapseThree" ? "" : "collapsed"}`}
                        type="button"
                        onClick={() => toggleDoc("collapseThree")}
                        aria-expanded={openDoc === "collapseThree"}
                        aria-controls="collapseThree"
                      >
                        <i class="bi bi-file-earmark-richtext me-1"></i>TF UDC
                        {limitSettingData["tf_udc_doc"]?.isUploaded && (
                          <i
                            class="bi bi-check-lg text-end ms-4 fs-5 fw-bold"
                            style={{ color: "rgb(4,147,4)" }}
                          ></i>
                        )}
                      </button>
                    </h2>
                    <div
                      id="collapseThree"
                      className={`accordion-collapse collapse ${openDoc === "collapseThree" ? "show" : ""}`}
                    >
                      <div className="accordion-body">
                        <div
                          className="p-3 border rounded"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <div className="row">
                            {data?.businessType === "individual" ? (
                              <div className="col-md-6 mb-3">
                                <label className="form-label">
                                  Person Name
                                </label>
                                <input
                                  className="form-control"
                                  type="text"
                                  name="personName"
                                  value={formData?.personName}
                                  placeholder="Person Name"
                                  onChange={handleChange}
                                />
                              </div>
                            ) : (
                              <>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Dealer Name
                                  </label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="dealerName"
                                    value={
                                      formData?.dealerName !== undefined
                                        ? formData?.dealerName
                                        : formData?.personName || ""
                                    }
                                    placeholder="Dealer Name"
                                    onChange={handleChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Company Name
                                  </label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="companyName"
                                    value={formData?.companyName}
                                    placeholder="Company Name"
                                    onChange={handleChange}
                                  />
                                </div>
                              </>
                            )}
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Account Name</label>
                              <input
                                className="form-control"
                                type="text"
                                name="account_name"
                                value={formData?.account_name}
                                placeholder="Enter account name"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Account No.</label>
                              <input
                                className="form-control"
                                type="number"
                                name="account_no"
                                value={formData?.account_no}
                                placeholder="Enter account number"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Bank Name</label>
                              <input
                                className="form-control"
                                type="text"
                                name="bank_name"
                                value={formData?.bank_name}
                                placeholder="Enter bank name"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Branch</label>
                              <input
                                className="form-control"
                                type="text"
                                name="branch"
                                value={formData?.branch}
                                placeholder="Enter branch"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">
                                Cheque Number 1
                              </label>
                              <input
                                className="form-control"
                                type="number"
                                name="tf_cheque_number"
                                value={formData?.tf_cheque_number}
                                placeholder="Enter cheque number 1"
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">
                                Cheque Number 2
                              </label>
                              <input
                                className="form-control"
                                type="number"
                                name="tf_cheque_number_2"
                                value={formData?.tf_cheque_number_2}
                                placeholder="Enter cheque number 2"
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap justify-content-between gap-2 mt-3">
                          <div className="btn-group">
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handlePreviewPDFForTFUDC}
                            >
                              <i className="bi bi-eye me-2"></i>
                              Preview
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={handleDownloadPDFTFUDC}
                            >
                              <i className="bi bi-download me-2"></i>
                              Download
                            </button>
                          </div>
                          <label
                            htmlFor={"tf_udc_doc"}
                            className="btn btn-success"
                          >
                            <i className="bi bi-upload me-2"></i>
                            Upload Document
                            <input
                              type="file"
                              id={"tf_udc_doc"}
                              name={"tf_udc_doc"}
                              style={{ display: "none" }}
                              onChange={(e) =>
                                handleUploadDPNDocument(e, "tf_udc_doc")
                              }
                            />
                          </label>
                        </div>

                        {limitSettingData["tf_udc_doc"]?.isUploaded && (
                          <div className="mt-3 alert alert-success d-flex justify-content-between align-items-center">
                            <div>
                              <i className="bi bi-file-earmark-check me-2"></i>
                              <strong>
                                {limitSettingData["tf_udc_doc"].documentName ===
                                  "tf_udc_doc" && "TF UDC Documents: Uploaded"}
                              </strong>
                            </div>
                            <div>
                              <ViewButton
                                file={
                                  limitSettingData["tf_udc_doc"].documentPath
                                }
                              />
                              <span className="mx-1">|</span>
                              <DownloadButton
                                file={
                                  limitSettingData["tf_udc_doc"].documentPath
                                }
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Other Documents Section */}
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button ${openDoc === "collapseFour" ? "" : "collapsed"}`}
                        type="button"
                        onClick={() => toggleDoc("collapseFour")}
                        aria-expanded={openDoc === "collapseFour"}
                        aria-controls="collapseFour"
                      >
                        <i class="bi bi-file-earmark-richtext me-1"></i> Other
                        Documents
                      </button>
                    </h2>
                    <div
                      id="collapseFour"
                      className={`accordion-collapse collapse ${openDoc === "collapseFour" ? "show" : ""}`}
                    >
                      <div className="accordion-body">
                        <div className="row g-3">
                          {[
                            {
                              name: "gst_declaration",
                              label: "GST Declaration",
                            },
                            // {
                            //   name: "signature_verification",
                            //   label: "Signature Verification",
                            // },
                            {
                              name: "name_declaration",
                              label: "Dual Name Declaration",
                            },
                            {
                              name: "dob_declaration",
                              label: "DOB Declaration",
                            },
                            {
                              name: "partners_authorization_letter",
                              label: "Partners Authorization Letter",
                            },
                            { name: "other_doc", label: "Other Document" },
                          ].map((doc) => (
                            <div
                              className="col-lg-4 col-md-6 col-sm-12"
                              key={doc.name}
                            >
                              <div
                                className="p-3 border rounded text-center h-100 d-flex flex-column justify-content-between"
                                style={{ backgroundColor: "#f8f9fa" }}
                              >
                                <label
                                  htmlFor={doc.name}
                                  className="btn btn-outline-primary w-100"
                                >
                                  <i className="bi bi-upload me-2"></i>
                                  {doc.label}
                                </label>
                                <input
                                  type="file"
                                  id={doc.name}
                                  name={doc.name}
                                  style={{ display: "none" }}
                                  onChange={(e) => handleUpload(e, doc.name)}
                                />
                                {limitSettingData[doc.name]?.isUploaded && (
                                  <div className="mt-3 alert alert-success text-center">
                                    <i className="bi bi-check-circle-fill me-2"></i>
                                    <strong>Uploaded</strong>
                                    <div className="mt-2">
                                      <ViewButton
                                        file={
                                          limitSettingData[doc.name]
                                            .documentPath
                                        }
                                      />
                                      <span className="mx-1">|</span>
                                      <DownloadButton
                                        file={
                                          limitSettingData[doc.name]
                                            .documentPath
                                        }
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            <div className="mt-2 d-flex flex-wrap justify-content-end">
              <button
                className="btn btn-secondary"
                onClick={handleSubmitLimitSettingDoc}
              >
                Mark As Completed
              </button>
            </div>
          </div>
        </>
      )}

      {/* ===== Merge PDF Modal ===== */}
      {showMergeModal && (
        <>
          {/* Backdrop */}
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1050 }}
            onClick={() => !mergingPdf && setShowMergeModal(false)}
          />
          {/* Modal */}
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ zIndex: 1055 }}
            onClick={(e) =>
              e.target === e.currentTarget && !mergingPdf && setShowMergeModal(false)
            }
          >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">

                {/* Header */}
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title mb-0">
                      <i className="bi bi-file-earmark-zip me-2 text-primary"></i>
                      Merge Documents
                    </h5>
                    <small className="text-muted">
                      Step {mergeStep} of 2 &middot;{" "}
                      {mergeStep === 1
                        ? "Add documents, set their order and who e-signs each one"
                        : "Upload the stamp paper, review the merged PDF and confirm"}
                    </small>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => !mergingPdf && setShowMergeModal(false)}
                    aria-label="Close"
                  />
                </div>

                {/* Body */}
                <div className="modal-body" style={{ height: "min(520px, 70vh)" }}>
                  {mergeStep === 1 ? (
                  <div className="row g-3 h-100">

                    {/* Left — Available Documents */}
                    <div className="col-12 col-md-6">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div>
                          <h6 className="mb-0">Available Documents</h6>
                          <small className="text-muted">Click a document to add it</small>
                        </div>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          disabled={
                            allMergeDocs.filter(
                              (d) => d.show && !docOrder.includes(d.key)
                            ).length === 0
                          }
                          onClick={() => {
                            const unselected = allMergeDocs
                              .filter((d) => d.show && !docOrder.includes(d.key))
                              .map((d) => d.key);
                            setDocOrder((prev) => [...prev, ...unselected]);
                          }}
                        >
                          Add All
                        </button>
                      </div>

                      <div className="d-flex flex-wrap gap-2">
                        {allMergeDocs.filter(
                          (d) => d.show && !docOrder.includes(d.key)
                        ).length === 0 ? (
                          <p className="text-muted fst-italic small mb-0 p-1">
                            All documents added to sequence
                          </p>
                        ) : (
                          allMergeDocs
                            .filter((d) => d.show && !docOrder.includes(d.key))
                            .map((doc) => (
                              <button
                                key={doc.key}
                                className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                                onClick={() => toggleDocSelection(doc.key)}
                              >
                                <i className="bi bi-plus-circle"></i>
                                {doc.label}
                              </button>
                            ))
                        )}
                      </div>
                    </div>

                    {/* Right — Merge Sequence */}
                    <div className="col-12 col-md-6 d-flex flex-column h-100">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div>
                          <h6 className="mb-0">Merge Sequence</h6>
                          <small className="text-muted">PDF pages appear in this order</small>
                        </div>
                        {docOrder.length > 0 && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            disabled={mergingPdf}
                            onClick={resetMergeSelection}
                          >
                            Clear All
                          </button>
                        )}
                      </div>

                      {/* Stamp paper is always first and signed by both — pinned, not movable. */}
                      <div className="list-group mb-2">
                        <div className="list-group-item d-flex align-items-center gap-2 py-2 px-3 bg-light">
                          <span
                            className="badge bg-secondary rounded-pill flex-shrink-0"
                            style={{ minWidth: "24px", textAlign: "center" }}
                            title="Always first"
                          >
                            <i className="bi bi-lock-fill"></i>
                          </span>
                          <div className="flex-grow-1">
                            <div className="fw-semibold" style={{ fontSize: "0.95rem" }}>
                              Stamp Paper{" "}
                              <span className="text-muted fw-normal" style={{ fontSize: "0.75rem" }}>
                                &middot; always first &middot;{" "}
                                {stampPaperFile || stampPaperLink ? (
                                  <span className="text-success">uploaded</span>
                                ) : (
                                  "upload in the next step"
                                )}
                              </span>
                            </div>
                            <div className="d-flex flex-wrap gap-3 mt-1" style={{ fontSize: "0.7rem" }}>
                              <span className="text-muted">E-Sign required by:</span>
                              <div className="form-check m-0">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="signer_buyer_stampPaper"
                                  checked
                                  onChange={() => {}}
                                  style={{ cursor: "default" }}
                                />
                                <label className="form-check-label" htmlFor="signer_buyer_stampPaper">
                                  Buyer
                                </label>
                              </div>
                              <div className="form-check m-0">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="signer_rh_stampPaper"
                                  checked
                                  onChange={() => {}}
                                  style={{ cursor: "default" }}
                                />
                                <label className="form-check-label" htmlFor="signer_rh_stampPaper">
                                  RH ({risk_head_role})
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {docOrder.length === 0 ? (
                        <div
                          className="border rounded-2 d-flex align-items-center justify-content-center text-muted"
                          style={{
                            minHeight: "130px",
                            backgroundColor: "#f8f9fa",
                            borderStyle: "dashed",
                          }}
                        >
                          <div className="text-center px-3">
                            <i className="bi bi-arrow-left-circle fs-3 d-block mb-1 text-secondary"></i>
                            <small>Click documents on the left to add them here</small>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="list-group"
                          style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
                        >
                          {docOrder.map((key, idx) => {
                            const doc = allMergeDocs.find((d) => d.key === key);
                            if (!doc) return null;
                            return (
                              <div
                                key={key}
                                className="list-group-item d-flex align-items-center gap-2 py-2 px-3"
                              >
                                <span
                                  className="badge bg-primary rounded-pill flex-shrink-0"
                                  style={{ minWidth: "24px", textAlign: "center" }}
                                >
                                  {idx + 1}
                                </span>
                                <div className="flex-grow-1">
                                  <div className="fw-semibold" style={{ fontSize: "0.95rem" }}>{doc.label}</div>
                                  <div
                                    className="d-flex flex-wrap gap-3 mt-1"
                                    style={{ fontSize: "0.7rem" }}
                                  >
                                    <span className="text-muted">E-Sign required by:</span>
                                    <div className="form-check m-0" title="The buyer signs every document">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={`signer_buyer_${key}`}
                                        checked
                                        onChange={() => {}}
                                        style={{ cursor: "default" }}
                                      />
                                      <label className="form-check-label" htmlFor={`signer_buyer_${key}`}>
                                        Buyer
                                      </label>
                                    </div>
                                    <div className="form-check m-0">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={`signer_rh_${key}`}
                                        checked={needsRhSign(key)}
                                        disabled={mergingPdf}
                                        onChange={() => toggleRhSign(key)}
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor={`signer_rh_${key}`}
                                        style={{ cursor: "pointer" }}
                                      >
                                        RH ({risk_head_role})
                                      </label>
                                    </div>
                                  </div>
                                </div>
                                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                  <button
                                    className="btn btn-sm btn-light border p-1 lh-1"
                                    title="Move up"
                                    disabled={idx === 0 || mergingPdf}
                                    onClick={() => moveDocUp(key)}
                                  >
                                    <i className="bi bi-chevron-up"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-light border p-1 lh-1"
                                    title="Move down"
                                    disabled={idx === docOrder.length - 1 || mergingPdf}
                                    onClick={() => moveDocDown(key)}
                                  >
                                    <i className="bi bi-chevron-down"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-light border p-1 lh-1 text-danger"
                                    title="Remove"
                                    disabled={mergingPdf}
                                    onClick={() => toggleDocSelection(key)}
                                  >
                                    <i className="bi bi-x-lg"></i>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                  </div>
                  ) : (
                  <>
                  {/* Summary of step 1 */}
                  <div className="border rounded-2 bg-light p-2 mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <small className="text-muted">
                        Merge sequence &middot;{" "}
                        <span className="badge rounded-pill bg-warning-subtle text-warning-emphasis border">
                          RH
                        </span>{" "}
                        = E-Sign also required by {risk_head_role}
                      </small>
                      <span
                        className="link-primary small"
                        style={{ cursor: "pointer" }}
                        onClick={() => !mergingPdf && setMergeStep(1)}
                      >
                        <i className="bi bi-pencil me-1"></i>Change
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {[
                        { key: "stampPaper", label: "Stamp Paper", rh: true },
                        ...docOrder
                          .map((key) => {
                            const doc = allMergeDocs.find((d) => d.key === key);
                            return doc ? { key, label: doc.label, rh: needsRhSign(key) } : null;
                          })
                          .filter(Boolean),
                      ].map((item, idx) => (
                        <span
                          key={item.key}
                          className="badge rounded-pill bg-white text-dark border d-inline-flex align-items-center gap-1 fw-normal"
                          style={{ fontSize: "0.8rem", padding: "0.4rem 0.65rem" }}
                        >
                          <span className="badge rounded-pill bg-primary" style={{ fontSize: "0.65rem" }}>
                            {idx + 1}
                          </span>
                          {item.label}
                          {item.rh && (
                            <span
                              className="badge rounded-pill bg-warning-subtle text-warning-emphasis border"
                              style={{ fontSize: "0.65rem" }}
                            >
                              RH
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* 1. Stamp paper */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold mb-0" htmlFor="ls_stamp_paper">
                      Stamp Paper (PDF) <span className="text-danger">*</span>
                    </label>
                    <div className="form-text mt-0 mb-2">
                      Comes first in the merged PDF. E-Sign required by the Buyer and RH ({risk_head_role}).
                    </div>
                    {/* Native input stays mounted (hidden once uploaded) so Replace can reuse it. */}
                    <input
                      ref={stampPaperInputRef}
                      type="file"
                      id="ls_stamp_paper"
                      accept="application/pdf"
                      className={`form-control form-control-sm ${
                        stampPaperLink || uploadingStampPaper ? "d-none" : ""
                      }`}
                      disabled={uploadingStampPaper || mergingPdf}
                      onChange={handleStampPaperUpload}
                    />
                    {uploadingStampPaper ? (
                      <div className="border rounded-2 px-3 py-2 small text-muted">
                        <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                        Uploading stamp paper...
                      </div>
                    ) : stampPaperLink ? (
                      <div className="border rounded-2 px-3 py-2 d-flex align-items-center gap-2">
                        <i className="bi bi-file-earmark-pdf-fill text-danger fs-5"></i>
                        <div className="flex-grow-1 small" style={{ minWidth: 0 }}>
                          <div
                            className="fw-medium"
                            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            {stampPaperFile ? stampPaperFile.name : "Stamp paper"}
                          </div>
                          <div className="text-success" style={{ fontSize: "0.75rem" }}>
                            <i className="bi bi-check-circle-fill me-1"></i>Uploaded
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={handleOpenStampPaper}
                        >
                          <i className="bi bi-eye me-1"></i>Open
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          disabled={mergingPdf}
                          onClick={() => stampPaperInputRef.current?.click()}
                        >
                          <i className="bi bi-arrow-repeat me-1"></i>Replace
                        </button>
                      </div>
                    ) : null}
                    <div className="mt-2" style={{ maxWidth: 260 }}>
                      <label className="form-label small fw-semibold mb-1" htmlFor="ls_stamp_paper_expiry">
                        Stamp Paper Expiry <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        id="ls_stamp_paper_expiry"
                        className={`form-control form-control-sm ${stampPaperExpiry ? "" : "is-invalid"}`}
                        value={stampPaperExpiry}
                        onChange={(e) => setStampPaperExpiry(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* 2. View / Download merged */}
                  <div className="mb-3">
                    <div className="fw-semibold mb-1">Merged Document</div>
                    {mergingPdf ? (
                      <span className="text-muted small">
                        <span className="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
                        Merging...
                      </span>
                    ) : (
                      <div className="d-flex gap-3 align-items-center">
                        <span
                          className={`text-nowrap ${canBuildMerged ? "link-primary" : "text-muted"}`}
                          style={{ cursor: canBuildMerged ? "pointer" : "not-allowed" }}
                          onClick={handleDownloadMergedPDF}
                        >
                          <i className="bi bi-cloud-arrow-down-fill"></i> Download
                        </span>
                        <span
                          className={`text-nowrap ${canBuildMerged ? "link-primary" : "text-muted"}`}
                          style={{ cursor: canBuildMerged ? "pointer" : "not-allowed" }}
                          onClick={handleViewMergedPDF}
                        >
                          <i className="bi bi-eye-fill"></i> View
                        </span>
                        {mergedViewed && (
                          <small className="text-success">
                            <i className="bi bi-check2"></i> Reviewed
                          </small>
                        )}
                      </div>
                    )}
                    {!canBuildMerged && (
                      <div className="form-text">Upload the stamp paper first.</div>
                    )}
                  </div>

                  {/* 3. Buyer name as per Aadhaar */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold mb-1" htmlFor="ls_buyer_aadhar_name">
                      Buyer Name As Per Aadhar
                    </label>
                    <input
                      type="text"
                      id="ls_buyer_aadhar_name"
                      className={`form-control form-control-sm ${buyerAadharName.trim() ? "" : "is-invalid"}`}
                      value={buyerAadharName}
                      onChange={(e) => setBuyerAadharName(e.target.value)}
                      placeholder="Enter the buyer name exactly as on Aadhaar"
                    />
                    <div className="form-text">
                      Used for the Aadhaar OTP e-sign. It must match Aadhaar exactly.
                    </div>
                  </div>

                  {/* 4. Consent */}
                  <div className="d-flex gap-2 align-items-start">
                    <input
                      type="checkbox"
                      id="ls_esign_consent"
                      className="form-check-input m-0 mt-1"
                      checked={esignConsent}
                      onChange={() => {
                        if (!mergedViewed) {
                          errorToast("Please view or download the merged document before confirming");
                          return;
                        }
                        setEsignConsent((prev) => !prev);
                      }}
                    />
                    <label htmlFor="ls_esign_consent" className="small" style={{ cursor: "pointer" }}>
                      I have reviewed the merged Limit Setting documents, including the stamp paper, and
                      confirm the selected signers and the buyer name as per Aadhaar are correct and ready
                      to be sent for e-sign.
                    </label>
                  </div>
                  </>
                  )}
                </div>

                {/* Footer */}
                <div className="modal-footer">
                  {mergeStep === 1 ? (
                    <>
                      <small className="text-muted me-auto">
                        {docOrder.length} document(s) selected
                      </small>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={mergingPdf}
                        onClick={() => setShowMergeModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary d-flex align-items-center gap-2"
                        disabled={docOrder.length === 0}
                        onClick={() => setMergeStep(2)}
                      >
                        Next
                        <i className="bi bi-arrow-right"></i>
                      </button>
                    </>
                  ) : (
                    <>
                      <small
                        className={`me-auto ${
                          esignMissing.length ? "text-danger" : esign.signed ? "text-warning" : "text-success"
                        }`}
                      >
                        {esignMissing.length ? (
                          <>Missing: {esignMissing.join(" · ")}</>
                        ) : esign.signed ? (
                          <>
                            <i className="bi bi-exclamation-triangle-fill me-1"></i>
                            Already e-signed. Resending will replace the signed document.
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle-fill me-1"></i>Ready to send
                          </>
                        )}
                      </small>
                      <button
                        type="button"
                        className="btn btn-secondary d-flex align-items-center gap-2"
                        disabled={mergingPdf || sendingEsign}
                        onClick={() => setMergeStep(1)}
                      >
                        <i className="bi bi-arrow-left"></i>
                        Back
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary d-flex align-items-center gap-2"
                        disabled={!esignReady}
                        onClick={handleSendForEsign}
                      >
                        {sendingEsign ? (
                          <>
                            <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                            Sending...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-pen"></i>
                            {esign.requestSent ? "Resend for E-Sign" : "Send for E-Sign"}
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>

              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default LimitSettingDocumentCollection;
